import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { ApiError, account, type GearItem } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import { ensurePaletteStyles } from "../../lib/categories";
import { useBodyClass } from "../login/useBodyClass";
import { GearListView, type GridActions, type GridLine } from "./components/GearListView";
import { HelpView } from "./components/HelpView";
import { ListsView } from "./components/ListsView";
import { NavigationView, type NavItem } from "./components/NavigationView";
import type { Selection } from "./components/InfoDisplay";
import { SettingsView } from "./components/SettingsView";
import { useGearMutations, useItems, useListDetail, useLists } from "./data";
import "../../styles/legacy/LibraryView.css";

export function LibraryPage() {
  useBodyClass("library-page");
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const params = useParams();
  const listId = params["*"]?.startsWith("list/") ? params["*"].slice(5) : null;
  const [nav, setNav] = useState<NavItem | null>(listId ? null : "Library");
  const [selection, setSelection] = useState<Selection>([]);
  const [unitId, setUnitId] = useState(user?.default_unit_id ?? 1);
  const items = useItems();
  const lists = useLists();
  const detail = useListDetail(listId);
  const m = useGearMutations();

  useEffect(ensurePaletteStyles, []);
  useEffect(() => {
    if (user) setUnitId(user.default_unit_id);
  }, [user?.default_unit_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const libraryLines: GridLine[] = useMemo(
    () => (items.data ?? []).map((item) => ({ key: item.id, item, line: null, quantity: 1, worn: item.worn, consumable: item.consumable, checked: false })),
    [items.data],
  );
  const listLines: GridLine[] = useMemo(() => {
    if (!detail.data) return [];
    const byId = new Map(detail.data.items.map((i) => [i.id, i]));
    return detail.data.lines.flatMap((line) => {
      const item = byId.get(line.item_id);
      return item ? [{ key: line.id, item, line, quantity: line.quantity, worn: line.worn, consumable: line.consumable, checked: line.checked }] : [];
    });
  }, [detail.data]);

  const showList = !nav && listId;
  const title = showList ? (detail.data?.list.name ?? "") : "Gear Library";
  const lines = showList ? listLines : libraryLines;

  function pickUnit(id: number) {
    setUnitId(id);
    if (user) account.update({ email: user.email, first_name: user.first_name, last_name: user.last_name, default_unit_id: id, show_name_on_shared: user.show_name_on_shared }).then(setUser).catch(() => {});
  }

  const actions: GridActions = {
    updateItem: (id, fields) => m.updateItem.mutate({ id, ...stripItem(fields, items.data?.find((i) => i.id === id)) }),
    updateLine: (line, fields) => m.updateLine.mutate({ id: line.id, quantity: line.quantity, worn: line.worn, checked: line.checked, consumable: line.consumable, ...fields }),
    addItem: async () => {
      try {
        const item = await m.createItem.mutateAsync({ name: "New Item", grams: 0, unit_id: unitId });
        if (showList && listId) m.addLine.mutate({ listId, item_id: item.id });
      } catch (e) {
        alert(e instanceof ApiError ? e.message : "Could not add the item");
      }
    },
    deleteRows: (rows) => {
      for (const r of rows) {
        if (showList && r.line) m.deleteLine.mutate(r.line.id);
        else m.deleteItem.mutate(r.item.id);
      }
    },
    addToList: (itemId) => listId && m.addLine.mutate({ listId, item_id: itemId }),
    importRows: async (rows) => {
      try {
        const created = await m.createItems.mutateAsync(rows);
        if (showList && listId) for (const c of created) m.addLine.mutate({ listId, item_id: c.id });
      } catch (e) {
        alert(e instanceof ApiError ? e.message : "Import failed");
      }
    },
  };

  const shareUrl = showList && detail.data ? `${window.location.origin}/list/${detail.data.list.id}` : undefined;

  if (!user) return null;
  return (
    <div className="LibraryView">
      <div className="controls">
        <h2>GearGrams</h2>
        <NavigationView
          selected={nav}
          onSelect={(n) => {
            setNav(n);
            navigate("/library");
          }}
        />
        <h2>Lists</h2>
        <ListsView
          lists={lists.data ?? []}
          selectedId={showList ? listId : null}
          selection={selection}
          unitId={unitId}
          onSelect={(id) => {
            setNav(null);
            navigate(`/library/list/${id}`);
          }}
          onAdd={(name) => m.createList.mutateAsync({ name }).then((l) => (setNav(null), navigate(`/library/list/${l.id}`)))}
          onRename={(id, name) => m.updateList.mutate({ id, name })}
          onDelete={(id) => m.deleteList.mutateAsync(id).then(() => (setNav("Library"), navigate("/library")))}
          onDropItems={(lid, ids) => ids.forEach((item_id) => m.addLine.mutate({ listId: lid, item_id }))}
          onLogout={() => logout().then(() => navigate("/login"))}
        />
      </div>
      <div className="listArea">
        {nav === "Settings" && <SettingsView user={user} onUserChange={setUser} onDeleted={() => (setUser(null), navigate("/login"))} />}
        {nav === "Help" && <HelpView user={user} />}
        {(nav === "Library" || showList) && (
          <GearListView
            title={title}
            mode={showList ? "list" : "library"}
            lines={lines}
            libraryItems={items.data ?? []}
            unitId={unitId}
            onUnitChange={pickUnit}
            onSelectionChange={(rows) => setSelection(rows.map((r) => ({ category: r.item.category, grams: r.item.grams, quantity: r.quantity, worn: r.worn, consumable: r.consumable })))}
            actions={actions}
            shareUrl={shareUrl}
          />
        )}
      </div>
    </div>
  );
}

/** The update endpoint replaces the whole item, so merge the edit into the current row. */
function stripItem(fields: Partial<GearItem>, current?: GearItem): Partial<GearItem> {
  const base = current
    ? { name: current.name, description: current.description, category: current.category, url: current.url, grams: current.grams, unit_id: current.unit_id, worn: current.worn, consumable: current.consumable }
    : {};
  return { ...base, ...fields };
}
